import * as React from "react";
import {
  AiQuotaService,
  type AIQuotaConfigResponse,
  type AIQuotaExceedAction,
  type AIQuotaUserRow
} from "../../../../../services/ai-quota-service";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaRoleBadge from "../../../../ui-kit/kuika-role-badge/kuika-role-badge";
import {
  KuikaSkeletonForm,
  KuikaSkeletonRegion,
  KuikaSkeletonTable
} from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTable, { type KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { extractBackendError, formatCreditAmount } from "../ai-chatbot-utils";
import TokenLimitOverrideModal from "./token-limit-override-modal";
import "./token-limits-tab.scss";

// ── Token Limits tab (KOA-18066) ────────────────────────────────────────────
// Owner-only management of per-user AI credit quotas: a workspace-default
// limits card + a members table with current-window consumption vs effective
// limits. All window math and status resolution is SERVER-side
// (AIWorkspaceController + AIQuotaMath) — this tab renders what the API says
// and refetches after every save.

/** Suggested starting values when the owner flips a limit switch on. */
const SUGGESTED_WEEKLY_LIMIT = 5000;
const SUGGESTED_MONTHLY_LIMIT = 20000;

interface DraftDefaults {
  weeklyEnabled: boolean;
  weeklyValue: number;
  monthlyEnabled: boolean;
  monthlyValue: number;
  exceedAction: AIQuotaExceedAction;
}

interface State {
  loading: boolean;
  loadError: string | null;
  config: AIQuotaConfigResponse | null;
  draft: DraftDefaults;
  saving: boolean;
  saveError: string | null;
  editingUser: AIQuotaUserRow | null;
}

function draftFromConfig(config: AIQuotaConfigResponse | null): DraftDefaults {
  const policy = config?.defaultPolicy ?? null;
  return {
    weeklyEnabled: policy?.weeklyLimitCredits != null,
    weeklyValue: policy?.weeklyLimitCredits ?? SUGGESTED_WEEKLY_LIMIT,
    monthlyEnabled: policy?.monthlyLimitCredits != null,
    monthlyValue: policy?.monthlyLimitCredits ?? SUGGESTED_MONTHLY_LIMIT,
    exceedAction: policy?.exceedAction === "warn" ? "warn" : "block"
  };
}

function draftsEqual(a: DraftDefaults, b: DraftDefaults): boolean {
  return (
    a.weeklyEnabled === b.weeklyEnabled &&
    a.monthlyEnabled === b.monthlyEnabled &&
    a.exceedAction === b.exceedAction &&
    (!a.weeklyEnabled || a.weeklyValue === b.weeklyValue) &&
    (!a.monthlyEnabled || a.monthlyValue === b.monthlyValue)
  );
}

function initialsOf(row: AIQuotaUserRow): string {
  const first: string = row.firstName?.trim()?.charAt(0) ?? "";
  const last: string = row.lastName?.trim()?.charAt(0) ?? "";
  const fallback: string = row.email?.trim()?.charAt(0) ?? "?";
  const letters: string = `${first}${last}` || fallback;
  // Locale-independent uppercase — identifier-safe (Turkish-i rule).
  return letters.toUpperCase();
}

function displayName(row: AIQuotaUserRow): string {
  const name: string = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
  return name.length > 0 ? name : row.email;
}

class TokenLimitsTab extends React.PureComponent<{}, State> {
  state: State = {
    loading: true,
    loadError: null,
    config: null,
    draft: draftFromConfig(null),
    saving: false,
    saveError: null,
    editingUser: null
  };

  private abortController: AbortController | null = null;

  private isUnmounted = false;

  async componentDidMount() {
    await this.loadOverview();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.abortController?.abort();
  }

  private safeSetState = (patch: Partial<State>) => {
    if (this.isUnmounted) return;
    this.setState(patch as State);
  };

  private loadOverview = async (): Promise<void> => {
    this.abortController?.abort();
    const controller: AbortController = new AbortController();
    this.abortController = controller;
    this.safeSetState({ loading: this.state.config === null, loadError: null });
    try {
      const config: AIQuotaConfigResponse = await AiQuotaService.GetQuotaOverview(controller.signal);
      if (controller.signal.aborted) return;
      this.safeSetState({ config, draft: draftFromConfig(config), loading: false, loadError: null });
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      this.safeSetState({ loading: false, loadError: extractBackendError(err) });
    }
  };

  // ── Default-limits card ──────────────────────────────────────────

  private handleSaveDefaults = async (): Promise<void> => {
    const { draft } = this.state;
    this.safeSetState({ saving: true, saveError: null });
    try {
      await AiQuotaService.UpdateWorkspaceQuotaConfig({
        weeklyLimitCredits: draft.weeklyEnabled ? Math.max(1, Math.round(draft.weeklyValue)) : null,
        monthlyLimitCredits: draft.monthlyEnabled ? Math.max(1, Math.round(draft.monthlyValue)) : null,
        exceedAction: draft.exceedAction
      });
      // Refetch instead of patching locally: the default change shifts every
      // member row's effective limits + status, which the server computes.
      await this.loadOverview();
      this.safeSetState({ saving: false });
    } catch (err: unknown) {
      this.safeSetState({ saving: false, saveError: extractBackendError(err) });
    }
  };

  private handleRevertDefaults = () => {
    this.setState((prev: State) => ({ draft: draftFromConfig(prev.config), saveError: null }));
  };

  private updateDraft(patch: Partial<DraftDefaults>): void {
    this.setState((prev: State) => ({ draft: { ...prev.draft, ...patch } }));
  }

  private handleLimitValueChange(field: "weeklyValue" | "monthlyValue", value: string | number): void {
    const parsed: number = typeof value === "number" ? value : parseInt(String(value), 10);
    if (Number.isNaN(parsed)) return;
    this.updateDraft({ [field]: Math.max(1, Math.round(parsed)) } as Partial<DraftDefaults>);
  }

  private renderLimitRow(
    label: string,
    unit: string,
    enabled: boolean,
    value: number,
    onToggle: (checked: boolean) => void,
    onValue: (value: string | number) => void
  ) {
    return (
      <div className="aichat-limits__row">
        <div className="aichat-limits__row-text">
          <div className="aichat-limits__row-label">{label}</div>
          <div className="aichat-limits__row-help">
            {enabled ? `Resets every ${unit === "week" ? "Monday 00:00 UTC" : "1st of the month (UTC)"}.` : "No cap."}
          </div>
        </div>
        <div className="aichat-limits__row-controls">
          {enabled && (
            <div className="aichat-limits__row-input">
              <KuikaInput
                type="number"
                size="md"
                min={1}
                value={String(value)}
                onChange={(_: React.ChangeEvent<HTMLInputElement>, next: string | number) => onValue(next)}
              />
              <span className="aichat-limits__unit">credits / {unit}</span>
            </div>
          )}
          <KuikaSwitch
            label={enabled ? "Limited" : "No limit"}
            checked={enabled}
            onChange={onToggle}
            dataTestId={`aiChatbotLimitSwitch-${unit}`}
          />
        </div>
      </div>
    );
  }

  private renderDefaultsCard() {
    const { draft, config, saving, saveError } = this.state;
    const dirty: boolean = config !== null && !draftsEqual(draft, draftFromConfig(config));

    // The hero stats mirror the DRAFT live — flipping a switch updates the
    // band before saving, so the card reads as one reactive instrument.
    const heroStats: { label: string; value: string; sub: string }[] = [
      {
        label: "Weekly cap",
        value: draft.weeklyEnabled ? formatCreditAmount(draft.weeklyValue) : "∞",
        sub: draft.weeklyEnabled ? "credits / week" : "no limit"
      },
      {
        label: "Monthly cap",
        value: draft.monthlyEnabled ? formatCreditAmount(draft.monthlyValue) : "∞",
        sub: draft.monthlyEnabled ? "credits / month" : "no limit"
      },
      {
        label: "On limit",
        value: draft.exceedAction === "block" ? "Block" : "Warn",
        sub: draft.exceedAction === "block" ? "chats pause" : "chats continue"
      }
    ];

    return (
      <div className="aichat-limits__policy">
        <div className="aichat-limits__policy-hero">
          <div className="aichat-limits__policy-hero-left">
            <span className="aichat-limits__pill">Workspace policy</span>
            <div className="aichat-limits__policy-title">Usage limits</div>
            <div className="aichat-limits__policy-sub">
              Applied to every member unless they have a custom override below.
            </div>
          </div>
          <div className="aichat-limits__policy-stats">
            {heroStats.map((stat) => (
              <div key={stat.label} className="aichat-limits__policy-stat">
                <span className="aichat-limits__policy-stat-label">{stat.label}</span>
                <span className="aichat-limits__policy-stat-value">{stat.value}</span>
                <span className="aichat-limits__policy-stat-sub">{stat.sub}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="aichat-limits__policy-body">
          {this.renderLimitRow(
            "Weekly limit",
            "week",
            draft.weeklyEnabled,
            draft.weeklyValue,
            (checked: boolean) => this.updateDraft({ weeklyEnabled: checked }),
            (value: string | number) => this.handleLimitValueChange("weeklyValue", value)
          )}
          {this.renderLimitRow(
            "Monthly limit",
            "month",
            draft.monthlyEnabled,
            draft.monthlyValue,
            (checked: boolean) => this.updateDraft({ monthlyEnabled: checked }),
            (value: string | number) => this.handleLimitValueChange("monthlyValue", value)
          )}

          <div className="aichat-limits__row">
            <div className="aichat-limits__row-text">
              <div className="aichat-limits__row-label">When a member hits their limit</div>
              <div className="aichat-limits__row-help">
                {draft.exceedAction === "block"
                  ? "Chats are refused until the window resets."
                  : "Chats continue — the member sees a warning."}
              </div>
            </div>
            <div className="aichat-limits__row-controls">
              <KuikaTabs
                size="sm"
                options={[
                  { value: "block", label: "Block" },
                  { value: "warn", label: "Warn" }
                ]}
                value={draft.exceedAction}
                onChange={(value: string) => this.updateDraft({ exceedAction: value === "warn" ? "warn" : "block" })}
                dataTestId="aiChatbotExceedAction"
              />
            </div>
          </div>

          {saveError && <div className="aichat-limits__save-error">{saveError}</div>}

          <div className="aichat-limits__actions">
            <span className="aichat-limits__actions-hint">
              {dirty ? "Unsaved changes — apply within ~10s of saving." : "Limits are measured in credits."}
            </span>
            <div className="aichat-limits__actions-buttons">
              <KuikaButton type="secondary" size="md" disabled={!dirty || saving} onClick={this.handleRevertDefaults}>
                Revert
              </KuikaButton>
              <KuikaButton
                type="primary"
                size="md"
                loading={saving}
                disabled={!dirty || saving}
                onClick={this.handleSaveDefaults}
                data-testid="aiChatbotLimitsSave"
              >
                Save
              </KuikaButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Members table ────────────────────────────────────────────────

  private renderMeter(consumed: number, limit: number | null) {
    if (limit === null) {
      return <div className="aichat-limits__usage-text">{formatCreditAmount(consumed)} · no limit</div>;
    }
    const ratio: number = limit > 0 ? consumed / limit : 0;
    const percent: number = Math.min(100, Math.round(ratio * 100));
    let modifier = "aichat-limits__meter-fill--ok";
    if (ratio >= 1) modifier = "aichat-limits__meter-fill--over";
    else if (ratio >= 0.8) modifier = "aichat-limits__meter-fill--warn";

    return (
      <div className="aichat-limits__usage-cell">
        <div className="aichat-limits__usage-text">
          {formatCreditAmount(consumed)} / {formatCreditAmount(limit)}
        </div>
        <div
          className="aichat-limits__meter"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span className={`aichat-limits__meter-fill ${modifier}`} style={{ width: `${percent}%` }} />
        </div>
      </div>
    );
  }

  private renderMemberCell(row: AIQuotaUserRow) {
    const name: string = displayName(row);
    return (
      <div className="aichat-limits__member">
        {row.profilePhotoUrl ? (
          <img className="aichat-limits__avatar" src={row.profilePhotoUrl} alt={`${name} avatar`} />
        ) : (
          <div className="aichat-limits__avatar aichat-limits__avatar--initials" aria-label={`${name} avatar`}>
            {initialsOf(row)}
          </div>
        )}
        <div className="aichat-limits__member-meta">
          <span className="aichat-limits__member-name">
            {name}
            {row.isDomainOwner && <KuikaRoleBadge role="Workspace Owner" label="Owner" />}
          </span>
          <span className="aichat-limits__member-email">{row.email}</span>
        </div>
      </div>
    );
  }

  private limitSourceLabel(row: AIQuotaUserRow): string {
    if (row.override?.isUnlimited) return "Unlimited";
    if (row.override) return "Custom";
    return "Default";
  }

  private buildColumns(): KuikaTableColumn<AIQuotaUserRow>[] {
    return [
      {
        id: "member",
        header: () => "Member",
        cell: (row: AIQuotaUserRow) => this.renderMemberCell(row),
        accessorFn: (row: AIQuotaUserRow): string => `${displayName(row)} ${row.email}`,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "text",
        width: 230
      },
      {
        id: "week",
        header: () => "This week",
        cell: (row: AIQuotaUserRow) =>
          row.effectiveIsUnlimited
            ? this.renderMeter(row.weeklyConsumedCredits, null)
            : this.renderMeter(row.weeklyConsumedCredits, row.effectiveWeeklyLimitCredits),
        accessorFn: (row: AIQuotaUserRow): number => row.weeklyConsumedCredits,
        enableSorting: true,
        width: 160
      },
      {
        id: "month",
        header: () => "This month",
        cell: (row: AIQuotaUserRow) =>
          row.effectiveIsUnlimited
            ? this.renderMeter(row.monthlyConsumedCredits, null)
            : this.renderMeter(row.monthlyConsumedCredits, row.effectiveMonthlyLimitCredits),
        accessorFn: (row: AIQuotaUserRow): number => row.monthlyConsumedCredits,
        enableSorting: true,
        width: 160
      },
      {
        id: "limits",
        header: () => "Limits",
        cell: (row: AIQuotaUserRow) => {
          const label: string = this.limitSourceLabel(row);
          let color: "purple" | "blue" | "grey" = "grey";
          if (label === "Unlimited") color = "purple";
          else if (label === "Custom") color = "blue";
          return <KuikaBadge content={label} color={color} size="small" />;
        },
        accessorFn: (row: AIQuotaUserRow): string => this.limitSourceLabel(row),
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 100
      },
      {
        id: "status",
        header: () => "Status",
        cell: (row: AIQuotaUserRow) => {
          switch (row.status) {
            case "blocked":
              return <KuikaBadge content="Blocked" color="red" size="small" />;
            case "warning":
              return <KuikaBadge content="Warning" color="yellow" size="small" />;
            case "unlimited":
              return <KuikaBadge content="Unlimited" color="purple" size="small" />;
            default:
              return <KuikaBadge content="OK" color="green" size="small" />;
          }
        },
        accessorFn: (row: AIQuotaUserRow): string => row.status,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "multiselect",
        width: 100
      },
      {
        id: "actions",
        header: () => "",
        cell: (row: AIQuotaUserRow) => (
          <KuikaButton
            type="secondary"
            size="sm"
            onClick={() => this.setState({ editingUser: row })}
            data-testid={`aiChatbotEditLimit-${row.email}`}
          >
            Edit
          </KuikaButton>
        ),
        enableSorting: false,
        width: 90,
        align: "right"
      }
    ];
  }

  // ── Render ───────────────────────────────────────────────────────

  private renderError() {
    return (
      <div className="aichat-limits__error">
        <div className="aichat-limits__error-icon" aria-hidden="true">
          <KuikaSvg name="info-circle" width={24} height={24} />
        </div>
        <div>
          <div className="aichat-limits__error-title">We couldn't load your workspace limits</div>
          <div className="aichat-limits__error-body">{this.state.loadError}</div>
        </div>
        <KuikaButton type="secondary" size="sm" onClick={this.loadOverview}>
          Retry
        </KuikaButton>
      </div>
    );
  }

  render() {
    const { loading, loadError, config, editingUser } = this.state;

    if (loadError && config === null) {
      return <div className="aichat-limits">{this.renderError()}</div>;
    }
    if (loading || config === null) {
      // The policy card's fields and the member table's rows, instead of a full-screen spinner over
      // the whole designer (L7). A reload after a save keeps the page: `loading` only rises without
      // a config.
      return (
        <div className="aichat-limits">
          <KuikaSkeletonRegion label="Loading usage limits">
            <div className="aichat-limits__skeleton">
              <KuikaSkeletonForm fields={3} />
              <KuikaSkeletonTable rows={5} header />
            </div>
          </KuikaSkeletonRegion>
        </div>
      );
    }

    const users: AIQuotaUserRow[] = config.users;

    return (
      <div className="aichat-limits">
        <div className="aichat-limits__intro">
          Keep AI spending predictable by capping how many credits each member can use per week or month. Members see a
          warning as they approach a limit; what happens at the limit is up to you.
        </div>

        {this.renderDefaultsCard()}

        <div className="aichat-limits__members-header">
          <div>
            <span className="aichat-limits__eyebrow">Per member</span>
            <h2 className="aichat-limits__members-title">
              Members <span className="aichat-limits__members-count">{users.length}</span>
            </h2>
          </div>
          <span className="aichat-limits__members-hint">
            {users.length === 1
              ? "You're the only member — invite teammates to manage their limits here."
              : "Click a member to set a custom limit or exempt them."}
          </span>
        </div>

        <KuikaTable<AIQuotaUserRow>
          data={users}
          columns={this.buildColumns()}
          rowKey={(row: AIQuotaUserRow): string => row.userId}
          pageSize={25}
          onRowClick={(row: AIQuotaUserRow) => this.setState({ editingUser: row })}
          enableSorting
          enableColumnFilters
          showCountLabel="members"
          emptyTitle="No members yet"
          emptyDescription="Invite teammates to your workspace to manage their AI usage limits."
        />

        <div className="aichat-limits__footnote">
          Weekly windows start Monday 00:00 UTC; monthly windows follow the calendar month. Changes can take up to ~10
          seconds to apply to active chats. Free Ask-mode conversations don't count against limits.
        </div>

        {editingUser && (
          <TokenLimitOverrideModal
            user={editingUser}
            onClose={() => this.setState({ editingUser: null })}
            onSaved={async () => {
              this.setState({ editingUser: null });
              await this.loadOverview();
            }}
          />
        )}
      </div>
    );
  }
}

export default TokenLimitsTab;
